import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Star, Plus } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";
import Img from "@/components/Img";

const RatingStars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`Rated ${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className="w-3 h-3"
        strokeWidth={1.5}
        fill={i <= Math.round(value) ? "#1A1A1A" : "none"}
        stroke={i <= Math.round(value) ? "#1A1A1A" : "#B3A394"}
      />
    ))}
  </span>
);

export const ProductCard = ({ product, index = 0 }) => {
  const { addToCart, toggleWishlist, isWishlisted } = useStore();
  const [activeColor, setActiveColor] = useState(0);
  const wished = isWishlisted(product.slug);
  const color = product.colors?.[activeColor];
  const mainImg = color?.image || product.images?.[0];
  const hoverImg = color?.hover || product.images?.[1] || mainImg;

  const quickAdd = (e) => {
    e.preventDefault();
    addToCart(product, { color: color?.name });
    toast.success("Added to bag", { description: product.name });
  };

  const wishClick = (e) => {
    e.preventDefault();
    toggleWishlist(product);
    toast(wished ? "Removed from wishlist" : "Saved to wishlist", { description: product.name });
  };

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col gap-3"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-white">
        <Img
          src={mainImg}
          alt={product.image_alts?.[0] || product.name}
          base={500}
          widths={[300, 500, 700]}
          sizes="(min-width:1024px) 25vw, 50vw"
          priority={index < 4}
          className="object-contain w-full h-full transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <Img
          src={hoverImg}
          alt=""
          aria-hidden="true"
          base={500}
          widths={[300, 500, 700]}
          sizes="(min-width:1024px) 25vw, 50vw"
          className="absolute inset-0 object-contain w-full h-full opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        />

        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.badges?.map((b) => (
            <span
              key={b}
              className="bg-lemce-ivory/95 text-lemce-matte text-[10px] px-2.5 py-1 tracking-widest uppercase"
            >
              {b}
            </span>
          ))}
          {product.discount_pct > 0 && (
            <span className="bg-lemce-matte text-lemce-ivory text-[10px] px-2.5 py-1 tracking-widest uppercase">
              {product.discount_pct}% Off
            </span>
          )}
        </div>

        <button
          onClick={wishClick}
          data-testid={`wishlist-btn-${product.slug}`}
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-lemce-ivory/90 backdrop-blur-sm hover:bg-lemce-ivory transition-colors"
        >
          <Heart className="w-4 h-4" strokeWidth={1.5} fill={wished ? "#1A1A1A" : "none"} stroke="#1A1A1A" />
        </button>

        <button
          onClick={quickAdd}
          data-testid={`quick-add-${product.slug}`}
          className="absolute bottom-3 left-3 right-3 flex items-center justify-center gap-2 bg-lemce-ivory/95 backdrop-blur-sm text-lemce-matte py-3 text-xs tracking-widest uppercase opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 hover:bg-lemce-matte hover:text-lemce-ivory"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Quick Add
        </button>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <RatingStars value={product.rating} />
          <span className="text-[11px] text-lemce-grey">({product.review_count})</span>
        </div>
        <h3 className="font-sans text-sm font-medium text-lemce-matte leading-snug">{product.name}</h3>
        <p className="text-xs text-lemce-grey line-clamp-1">{product.descriptor}</p>
        <div className="flex items-baseline gap-2 mt-0.5">
          <span className="text-sm font-medium text-lemce-matte">{product.price_display}</span>
          {product.discount_pct > 0 && (
            <span className="text-xs text-lemce-grey line-through">{product.mrp_display}</span>
          )}
        </div>
        {product.colors?.length > 1 && (
          <div className="flex gap-1.5 mt-1.5">
            {product.colors.map((c, i) => (
              <button
                key={c.name}
                onClick={(e) => { e.preventDefault(); setActiveColor(i); }}
                aria-label={c.name}
                className={`w-4 h-4 rounded-full border transition-transform ${
                  i === activeColor ? "border-lemce-matte scale-110" : "border-lemce-beige"
                }`}
                style={{ backgroundColor: c.hex }}
              />
            ))}
          </div>
        )}
      </div>
    </Link>
  );
};

export { RatingStars };
export default ProductCard;
